'use client'

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import {
  getPlayUser,
  setPlayUser,
  clearPlaySession,
  type PlayUser,
} from '@/lib/phishtrainer/play-api'

/**
 * Player auth — completely independent from admin auth.
 *
 * Session model (2026-06-04): localStorage-FIRST. The learner's recipient JWT +
 * identity live in `hf-play:*` localStorage keys (set by the /play/auth redeem
 * or SSO callback). This is the robust path — no httpOnly cookie means
 * incognito / Safari-Brave ITP / third-party-cookie blocking can't drop the
 * session and bounce the learner back to login. A cookie session (read via
 * /api/play/auth/session) remains as a fallback for any flow that only set the
 * cookie; when hit, its user is mirrored into localStorage.
 */

export interface PlayerUser {
  id: string
  email?: string
  name?: string
  orgId?: string
  isAnonymous: boolean
  isImpersonating?: boolean
}

interface PlayerAuthState {
  user: PlayerUser | null
  loading: boolean
  error?: string
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const PlayerAuthContext = createContext<PlayerAuthState | null>(null)

export function PlayerAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PlayerUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const refresh = async () => {
    // 1. localStorage is the source of truth — instant + cookie-independent.
    const local = getPlayUser()
    if (local) {
      setUser(local as PlayerUser)
      return
    }
    // 2. Fallback: cookie session (e.g. an SSO flow that only set the cookie).
    //    Mirror its user into localStorage so subsequent reads are instant.
    try {
      const res = await fetch('/api/play/auth/session', { cache: 'no-store' })
      if (!res.ok) {
        setUser(null)
        return
      }
      const json = await res.json()
      const u = (json.user || null) as PlayerUser | null
      setUser(u)
      if (u) setPlayUser(u as PlayUser)
    } catch {
      setUser(null)
    }
  }

  const signOut = async () => {
    clearPlaySession()
    try {
      await fetch('/api/play/auth/signout', { method: 'POST' })
    } finally {
      setUser(null)
      router.push('/play/auth')
    }
  }

  // Token-from-URL flow.
  // The dedicated /play/auth page OWNS the invite token: it decides between
  // MANDATORY Google/Microsoft SSO (when the workspace configured its OAuth app)
  // and a direct invite-redeem fallback. The provider must NOT auto-redeem the
  // token itself — doing so races the SSO gate and bypasses the requirement. So
  // any ?token= seen outside the auth page is forwarded to the auth page; on the
  // auth page (or with no token) we just check for an existing session.
  useEffect(() => {
    const token = searchParams?.get('token')
    const onAuthPage = (pathname || '').startsWith('/play/auth')
    const action = async () => {
      if (token && !onAuthPage) {
        // Forward to the PATH form (`/play/auth/<token>`) — survives Microsoft
        // Safe Links / gateway query-param stripping. The /play/auth/[token]
        // route hands the segment to PlayerAuthInner identically to the legacy
        // query form.
        router.replace(`/play/auth/${encodeURIComponent(token)}`)
        return
      }
      await refresh()
      setLoading(false)
    }
    action()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Route protection — unauthenticated users get redirected to /play/auth
  useEffect(() => {
    if (loading) return
    if (user) return
    if (!pathname) return
    // Public routes: auth page, framework explainer (no personal data)
    const publicRoutes = ['/play/auth', '/play/framework']
    if (publicRoutes.some((r) => pathname.startsWith(r))) return
    // Hydration-race guard: `user` state can be transiently null on a fresh
    // mount / right after an impersonation or invite-redeem navigation, while
    // the session ALREADY exists in localStorage (the synchronous source of
    // truth). Bouncing to /play/auth here is the bug that dropped learners and
    // "Play as this user" onto the SSO login mid-game. If a session blob is
    // present, hydrate it instead of redirecting.
    const local = getPlayUser()
    if (local) {
      setUser(local as PlayerUser)
      return
    }
    router.push('/play/auth')
  }, [loading, user, pathname, router])

  return (
    <PlayerAuthContext.Provider value={{ user, loading, error, signOut, refresh }}>
      {children}
    </PlayerAuthContext.Provider>
  )
}

export function usePlayerAuth() {
  const ctx = useContext(PlayerAuthContext)
  if (!ctx) throw new Error('usePlayerAuth must be used within PlayerAuthProvider')
  return ctx
}
